import { Level } from "../Level.js";
import { GRID_W, GRID_H } from "../../config/constants.js";
import { rndi, rnd, pick } from "../../core/util.js";
import { scatterProps, finalize, walkableCells, gdist } from "./helpers.js";

/**
 * Wilderness: an organic open clearing ringed by cliffs, thick with grass and
 * bushes, studded with large rock formations and tree clumps, and dotted with
 * abandoned CAMPS you can loot (breakable barrels/crates + a chest around a
 * campfire). finalize() guarantees a reachable exit and clears the entrance.
 */
export function generateWilderness(floor, theme) {
  const level = new Level(GRID_W, GRID_H, theme);
  const cx = GRID_W / 2, cy = GRID_H / 2;
  const rx = GRID_W / 2 - 3, ry = GRID_H / 2 - 3;

  // organic open ground (ellipse + a little raggedness)
  for (let y = 0; y < GRID_H; y++)
    for (let x = 0; x < GRID_W; x++) {
      const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (d <= 1 || (d <= 1.12 && Math.random() < 0.5)) level.setFloor(x, y, (x + y) % 2);
    }

  const startX = Math.floor(cx - rx + 2), startY = Math.floor(cy);
  level.playerStart = { gx: startX, gy: startY };

  // place a prop from the theme palette (key resolved from theme.assets, or overridden)
  const put = (gx, gy, shape, opts = {}) => {
    if (!level.hasFloorGrid(gx, gy)) return false;
    if (gdist(gx, gy, startX, startY) < 6) return false;
    level.addProp({
      gx: gx + 0.5, gy: gy + 0.5, shape, color: opts.color || 0x808080,
      blocking: !!opts.blocking, breakable: !!opts.breakable, table: opts.table,
      scale: opts.scale || 1, rot: Math.random() * Math.PI * 2,
      key: opts.key || (theme.assets && theme.assets[shape] ? theme.assets[shape] : null),
    });
    return true;
  };

  // ---- large rock formations (blocking cover) ----
  const forms = 6 + Math.min(floor, 6);
  for (let f = 0; f < forms; f++) {
    const fx = rndi(4, GRID_W - 4), fy = rndi(4, GRID_H - 4);
    if (!level.hasFloorGrid(fx, fy) || gdist(fx, fy, startX, startY) < 9) continue;
    put(fx, fy, pick(["boulder", "boulder2", "boulder3"]), { blocking: true, scale: rnd(1.3, 2.0) });
    const n = rndi(2, 5);
    for (let i = 0; i < n; i++) {
      const rxp = Math.round(fx + rnd(-2.2, 2.2)), ryp = Math.round(fy + rnd(-2.2, 2.2));
      put(rxp, ryp, pick(["boulder", "boulder_large", "rock", "rock2", "rock_sm"]), { blocking: Math.random() < 0.6, scale: rnd(0.8, 1.6) });
    }
  }

  // ---- tree clumps ----
  const clumps = 12 + Math.min(floor * 2, 14);
  for (let c = 0; c < clumps; c++) {
    const ccx = rndi(4, GRID_W - 4), ccy = rndi(4, GRID_H - 4);
    if (!level.hasFloorGrid(ccx, ccy) || gdist(ccx, ccy, startX, startY) < 8) continue;
    const n = rndi(3, 6);
    for (let i = 0; i < n; i++) {
      const px = Math.round(ccx + rnd(-2.5, 2.5)), py = Math.round(ccy + rnd(-2.5, 2.5));
      put(px, py, pick(["tree_pine", "tree_round", "tree_tall", "tree_pine", "deadtree"]), { blocking: true, scale: rnd(1.0, 1.5) });
    }
  }

  // ---- lootable camps ----
  const camps = 2 + Math.min(Math.floor(floor / 2), 3);
  for (let c = 0; c < camps; c++) {
    let px = 0, py = 0, ok = false;
    for (let t = 0; t < 24; t++) {
      px = rndi(6, GRID_W - 6); py = rndi(6, GRID_H - 6);
      if (level.hasFloorGrid(px, py) && gdist(px, py, startX, startY) > 11) { ok = true; break; }
    }
    if (!ok) continue;
    put(px, py, "torch", { key: theme.assets.campfire, blocking: false });          // campfire (warm light)
    put(px - 1, py, "bedroll", { key: theme.assets.bedroll, blocking: false });
    put(px + 1, py, "crate", { blocking: true, breakable: true, table: "crate" });
    put(px, py + 1, "barrel", { blocking: true, breakable: true, table: "barrel" });
    put(px - 1, py + 1, "barrel_large", { blocking: true, breakable: true, table: "barrel", scale: 1.1 });
    if (Math.random() < 0.75) put(px + 1, py - 1, "chest", { blocking: true, breakable: true, table: "chest" });
    put(px + 1, py + 1, "keg", { blocking: true });
    if (Math.random() < 0.6) put(px - 1, py - 1, "coins", { blocking: false, scale: 0.9 });
    put(px, py - 1, "rock_sm", { blocking: false, scale: 0.7 });
    // guards spawn at the camp
    level.spawnPoints.push({ gx: px + 0.5, gy: py + 0.5 }, { gx: px + 2.5, gy: py + 0.5 });
  }

  // ---- dense grass + bush ground cover ----
  const soft = { ...theme, props: theme.props.filter((p) => !p.blocking) };
  scatterProps(level, soft, walkableCells(level), theme.propDensity, [[startX, startY]]);

  // spawn candidates spread across the map, away from the entrance
  for (const [gx, gy] of walkableCells(level))
    if (gdist(gx, gy, startX, startY) > 8 && Math.random() < 0.5) level.spawnPoints.push({ gx: gx + 0.5, gy: gy + 0.5 });

  // clear a disc around the entrance
  for (let dy = -2; dy <= 2; dy++)
    for (let dx = -2; dx <= 2; dx++) {
      const x = startX + dx, y = startY + dy;
      if (level.hasFloorGrid(x, y)) level.block[y][x] = 0;
    }

  return finalize(level);
}
